import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import api from '../utils/api';
import './UserChat.css';

const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
  }
  return window.location.origin;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export default function UserChat({ onUnread }) {
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [connected, setConnected] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [authError, setAuthError] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  // Load history + connect socket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthError(true);
      return;
    }
    setAuthError(false);

    // Load REST history
    api.get('/chat/history')
      .then(res => setMessages(res.data.messages || []))
      .catch(() => {});

    // Connect socket
    const socket = io(getSocketUrl(), {
      auth: { token },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));
    socket.on('admin_online', () => setAdminOnline(true));
    socket.on('admin_offline', () => setAdminOnline(false));

    // Only add incoming admin messages — own messages are added optimistically on send
    socket.on('chat:message', (msg) => {
      if (!msg.fromAdmin) return; // own echo — already shown optimistically
      setMessages(prev => {
        if (prev.some(m => String(m._id) === String(msg._id))) return prev;
        return [...prev, msg];
      });
      if (onUnread) onUnread(c => c + 1);
    });

    return () => { socket.disconnect(); };
  }, [onUnread]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (!socketRef.current?.connected) return;

    // Show message immediately (optimistic)
    const tempMsg = {
      _id: 'temp_' + Date.now(),
      fromAdmin: false,
      message: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMsg]);
    setInput('');

    socketRef.current.emit('chat:send', { message: trimmed });
  }, [input]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Group messages by date
  const grouped = [];
  let lastDate = null;
  messages.forEach(msg => {
    const d = formatDate(msg.createdAt);
    if (d && d !== lastDate) { grouped.push({ type: 'date', label: d }); lastDate = d; }
    grouped.push({ type: 'msg', msg });
  });

  if (authError) {
    return (
      <div className="uc-root">
        <div className="uc-header">
          <div className="uc-header-info">
            <span className="uc-avatar">👤</span>
            <div>
              <p className="uc-name">Support</p>
              <p className="uc-status">Not signed in</p>
            </div>
          </div>
        </div>
        <div className="uc-messages">
          <p className="uc-empty">Please sign in with your wallet to access support chat.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="uc-root">
      <div className="uc-header">
        <div className="uc-header-info">
          <span className="uc-avatar">👤</span>
          <div>
            <p className="uc-name">Support</p>
            <p className={`uc-status ${adminOnline ? 'uc-online' : ''}`}>
              {adminOnline ? 'Admin online' : connected ? 'Connected — send a message' : 'Connecting…'}
            </p>
          </div>
        </div>
        <span className={`uc-conn-dot ${connected ? 'uc-conn-on' : ''}`} title={connected ? 'Connected' : 'Disconnected'} />
      </div>

      <div className="uc-messages">
        {grouped.length === 0 && (
          <p className="uc-empty">No messages yet. Send a message to get support from the admin.</p>
        )}
        {grouped.map((item, i) =>
          item.type === 'date'
            ? <div key={`d-${i}`} className="uc-date-divider">{item.label}</div>
            : (
              <div
                key={item.msg._id || i}
                className={`uc-bubble-row ${item.msg.fromAdmin ? 'uc-row-admin' : 'uc-row-user'}`}
              >
                {item.msg.fromAdmin && <span className="uc-bubble-avatar">⭐</span>}
                <div className={`uc-bubble ${item.msg.fromAdmin ? 'uc-bubble-admin' : 'uc-bubble-user'}`}>
                  <p className="uc-bubble-text">{item.msg.message}</p>
                  <span className="uc-bubble-time">{formatTime(item.msg.createdAt)}</span>
                </div>
              </div>
            )
        )}
        <div ref={bottomRef} />
      </div>

      <div className="uc-input-row">
        <textarea
          className="uc-input"
          placeholder={connected ? 'Type a message…' : 'Connecting…'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          maxLength={2000}
        />
        <button
          className="uc-send-btn"
          onClick={send}
          disabled={!input.trim() || !connected}
        >
          Send
        </button>
      </div>
    </div>
  );
}
